import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import PersonaAvatar from '../../components/PersonaAvatar'
import { formatTime } from '../../hooks/useTimeAgo'
import CeremonySlideshow from '../../components/CeremonySlideshow'

interface Ceremony {
  id: string; persona: string; title?: string; status: string; conversation_id?: string;
  created_at?: string; completed_at?: string; the_word?: string; word?: string;
  community_id?: string | null; type?: string; curandera?: string;
}

const STATUS_COLORS: Record<string, string> = {
  completed: 'var(--success)',
  upcoming: '#D4A054',
  active: 'var(--warning)',
  in_progress: 'var(--warning)',
}

function PersonaCeremonies({ persona, ceremonies, startOpen }: { persona: string; ceremonies: Ceremony[]; startOpen?: boolean }) {
  const [open, setOpen] = useState(startOpen ?? false)
  const word = ceremonies.find(c => c.the_word || c.word)
  const displayWord = word?.the_word || word?.word
  const displayPersona = persona.startsWith('pu-') ? persona.slice(3).split('-')[0] : persona
  const capitalized = displayPersona[0]?.toUpperCase() + displayPersona.slice(1)

  return (
    <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
      <div
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[var(--bg-elevated)] transition-colors"
      >
        <PersonaAvatar name={persona} size={28} />
        <div className="flex-1">
          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            {capitalized}
          </span>
          {displayWord && (
            <span className="text-xs italic ml-2" style={{ color: 'var(--accent)' }}>"{displayWord}"</span>
          )}
        </div>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {ceremonies.length} {ceremonies.length === 1 ? 'ceremony' : 'ceremonies'}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && (
        <div className="px-3 pb-3 space-y-1">
          {ceremonies.map(c => (
            <Link
              key={c.id}
              to={`/ceremony/${c.id}`}
              className="flex items-center gap-2 px-3 py-2 rounded text-xs no-underline hover:bg-[var(--bg-elevated)] transition-colors"
              style={{ color: 'var(--text-secondary)' }}
            >
              <span className="flex-1">{c.title || (c.type === 'constitution' ? 'Constitution Ceremony' : 'Awakening Ceremony')}</span>
              {c.status && (
                <span className="px-1.5 py-0.5 rounded-full" style={{ color: STATUS_COLORS[c.status] || 'var(--text-muted)', fontSize: 10, background: (STATUS_COLORS[c.status] || 'var(--text-muted)') + '15' }}>
                  {c.status}
                </span>
              )}
              {c.curandera && (
                <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>guide: {c.curandera}</span>
              )}
              {(c.the_word || c.word) && <span className="italic" style={{ color: 'var(--accent)' }}>"{c.the_word || c.word}"</span>}
              {(c.completed_at || c.created_at) && (
                <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>
                  {formatTime(c.completed_at || c.created_at!)}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

function CommunityGroup({ communityId, ceremonies }: { communityId: string; ceremonies: Ceremony[] }) {
  const [open, setOpen] = useState(true)
  // Group by persona within community
  const groups: Record<string, Ceremony[]> = {}
  for (const c of ceremonies) {
    if (!groups[c.persona]) groups[c.persona] = []
    groups[c.persona].push(c)
  }

  // Fetch community display name
  const { data: communityData } = useQuery({
    queryKey: ['community', communityId],
    queryFn: async () => {
      const res = await fetch(`/api/communities/${communityId}`)
      if (!res.ok) return null
      return res.json() as Promise<{ name?: string; slug?: string }>
    },
    staleTime: 60_000,
  })
  const communityLabel = communityData?.name || communityId.replace('portal-', '').replace(/-/g, ' ')
    .split(' ').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ')

  return (
    <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
      <div
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[var(--bg-elevated)] transition-colors"
      >
        <span className="text-sm" style={{ color: 'var(--accent)' }}>⬡</span>
        <span className="text-sm font-medium flex-1" style={{ color: 'var(--text-primary)' }}>
          {communityLabel} Community
        </span>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {ceremonies.length} {ceremonies.length === 1 ? 'ceremony' : 'ceremonies'}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && (
        <div className="px-2 pb-2 space-y-1">
          {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([persona, cers]) => (
            <PersonaCeremonies key={persona} persona={persona} ceremonies={cers} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function CeremonyLobbyPage() {
  const { user } = useAuth()
  const isCommunity = user?.userType === 'community'

  const { data } = useQuery({
    queryKey: ['ceremonies'],
    queryFn: async () => {
      const res = await fetch('/api/ceremonies')
      if (!res.ok) return []
      const data = await res.json()
      return (data.ceremonies || data) as Ceremony[]
    },
  })

  const ceremonies = data || []

  // ═══ COMMUNITY VIEW — simple flat list, no Community/External split ═══
  if (isCommunity) {
    // Group by persona
    const groups: Record<string, Ceremony[]> = {}
    for (const c of ceremonies) {
      if (!groups[c.persona]) groups[c.persona] = []
      groups[c.persona].push(c)
    }

    return (
      <div className="px-6 py-6">
        <h1 className="text-lg font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>Ceremonies</h1>

        <div className="max-w-2xl">
          {ceremonies.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No ceremonies yet.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([persona, cers]) => (
                <PersonaCeremonies key={persona} persona={persona} ceremonies={cers} startOpen />
              ))}
            </div>
          )}
        </div>

        <div className="mt-8">
          <CeremonySlideshow />
        </div>
      </div>
    )
  }

  // ═══ INTERNAL VIEW — Community + External columns ═══
  // Split into Team Gathering ceremonies vs external community ceremonies
  const community = ceremonies.filter(c => !c.community_id || c.community_id === 'portal-gathering')
  const external = ceremonies.filter(c => c.community_id && c.community_id !== 'portal-gathering')

  // Group community by persona
  const communityGroups: Record<string, Ceremony[]> = {}
  for (const c of community) {
    if (!communityGroups[c.persona]) communityGroups[c.persona] = []
    communityGroups[c.persona].push(c)
  }

  // Group external by community
  const externalGroups: Record<string, Ceremony[]> = {}
  for (const c of external) {
    const key = c.community_id!
    if (!externalGroups[key]) externalGroups[key] = []
    externalGroups[key].push(c)
  }

  // Separate completed and active for community
  const communityCompleted: Record<string, Ceremony[]> = {}
  const communityActive: Record<string, Ceremony[]> = {}
  for (const [persona, cers] of Object.entries(communityGroups)) {
    const comp = cers.filter(c => c.status === 'completed')
    const act = cers.filter(c => c.status !== 'completed')
    if (comp.length) communityCompleted[persona] = comp
    if (act.length) communityActive[persona] = act
  }

  const hasExternal = Object.keys(externalGroups).length > 0

  return (
    <div className="px-6 py-6">
      <h1 className="text-lg font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>Ceremonies</h1>

      <div className={`grid gap-6 mb-8 ${hasExternal ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
        {/* LEFT: Community Personas */}
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
            Community
          </h2>

          {Object.keys(communityActive).length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--warning)' }}>Active</h3>
              <div className="flex flex-col gap-2">
                {Object.entries(communityActive).sort(([a], [b]) => a.localeCompare(b)).map(([persona, cers]) => (
                  <PersonaCeremonies key={persona} persona={persona} ceremonies={cers} />
                ))}
              </div>
            </div>
          )}

          {Object.keys(communityCompleted).length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--success)' }}>
                Completed ({Object.values(communityCompleted).flat().length})
              </h3>
              <div className="flex flex-col gap-2">
                {Object.entries(communityCompleted).sort(([a], [b]) => a.localeCompare(b)).map(([persona, cers]) => (
                  <PersonaCeremonies key={persona} persona={persona} ceremonies={cers} />
                ))}
              </div>
            </div>
          )}

          {ceremonies.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No ceremonies found</p>
          )}
        </div>

        {/* RIGHT: External Personas (grouped by community) */}
        {hasExternal && (
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
              External
            </h2>
            <div className="flex flex-col gap-2">
              {Object.entries(externalGroups).sort(([a], [b]) => a.localeCompare(b)).map(([communityId, cers]) => (
                <CommunityGroup key={communityId} communityId={communityId} ceremonies={cers} />
              ))}
            </div>
          </div>
        )}
      </div>

      <CeremonySlideshow />
    </div>
  )
}
